import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Archive, Loader2, Lock } from 'lucide-react'
import api from '../../services/api'
import { formatDate, formatDateTime } from '../../utils/helpers'

/**
 * Archive & retention, inside a company: its finished dossiers in one place (GET /teams/archive).
 *
 * For the organisation administrator, and for a records keeper the dossiers whose rules name them --
 * nobody else is shown the menu item or anything here. Archiving is by hand only and is done on the
 * dossier itself (Archive, with the dossier number typed to confirm); this page says what is ready for
 * it, what is archived and kept until when, and what is held. Nothing is ever deleted from here.
 */

function Group({ title, hint, rows, empty }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-xs">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-800">{title} <span className="font-normal text-gray-500">· {rows.length}</span></h2>
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
      </div>
      {rows.length === 0
        ? <p className="px-4 py-4 text-sm text-gray-400">{empty}</p>
        : <ul className="divide-y divide-gray-100">
            {rows.map(d => (
              <li key={d.id} className="px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="min-w-0 mr-auto">
                  <p className="text-xs font-mono text-gray-500">{d.dossier_number}</p>
                  <Link to={`/teams/${d.team_id}?dossier=${d.id}`} className="text-sm font-medium text-gray-900 hover:underline">{d.title}</Link>
                  <p className="text-xs text-gray-500">
                    {d.team}
                    {d.submitted ? ` · submitted to ${d.submitted_to || '—'} on ${formatDate(d.submitted.at)}` : ''}
                    {d.closed_reason ? ` · closed unsent: “${d.closed_reason}”` : ''}
                    {d.archived ? ` · archived by ${d.archived.by} on ${formatDateTime(d.archived.at)}` : ''}
                  </p>
                </div>
                {d.legal_hold && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Legal hold
                  </span>
                )}
                <span className={`text-xs ${d.retention_ended ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                  {d.retention_ended ? 'Years to keep have run out — ' : 'Kept until '}{formatDate(d.retain_until)}
                </span>
              </li>
            ))}
          </ul>}
    </div>
  )
}

export default function CompanyArchive() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    api.get('/teams/archive').then(r => setData(r.data)).catch(() => setError('Could not load the archive.'))
  }, [])

  if (error) return <p className="p-6 text-sm text-red-600">{error}</p>
  if (!data) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>

  const ready = data.dossiers.filter(d => !d.archived)
  const archived = data.dossiers.filter(d => d.archived)
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Archive className="w-6 h-6 text-blue-600" /> Archive & retention</h1>
        <p className="text-sm text-gray-500 mt-1">
          Archiving is by hand only, never on a timer. Open a dossier and press Archive; it is sealed so it can be proven later
          that nothing changed. When the years to keep run out you are reminded — nothing is ever deleted by itself.
        </p>
      </div>
      <Group title="Ready to archive" hint="Submitted, or closed unsent with a reason."
             rows={ready} empty="Nothing is waiting to be archived." />
      <Group title="Archived" hint="Sealed and locked. Kept for the years their rules set."
             rows={archived} empty="No dossier has been archived yet." />
    </div>
  )
}
